import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { deleteAllCouponImages } from '@/lib/couponImageCleanup';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Gift, Percent, Calendar, Users, Trash2, Plus, Check, AlertTriangle, QrCode, Download, Copy, ExternalLink, RefreshCw, Share, Eye, Settings, Apple, Wallet, Pencil } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import CouponOfferBuilder from './CouponOfferBuilder';
import ShareDownloadDialog from './ShareDownloadDialog';
import UnifiedPassPreview from '@/components/loyalty/UnifiedPassPreview';
import BusinessScannerCredentialsManager from '@/components/business/BusinessScannerCredentialsManager';
import { PRIMARY_COLOR } from '@/lib/constants/colors';
import { UnifiedSkeleton } from '@/components/ui/unified-skeleton';

const BASE_URL = 'https://taginn.app';

interface CouponOffer {
  id: string;
  business_id: string;
  name: string;
  header?: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free_item';
  discount_value?: number;
  minimum_purchase?: number;
  valid_from: string;
  valid_until?: string;
  usage_limit_per_customer: number;
  total_usage_limit?: number;
  background_color?: string;
  logo_url?: string; // Deprecated - kept for backward compatibility
  hero_image_url?: string; // Deprecated - kept for backward compatibility
  apple_logo_urls?: { '1x': string; '2x': string; '3x': string };
  google_logo_url?: string;
  hero_image_urls?: { '1x': string; '2x': string; '3x': string };
  google_class_id?: string;
  is_active: boolean;
  auto_delete_used_tokens?: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

interface CouponOfferStats {
  id: string;
  total_redemptions: number;
  unique_customers: number;
  usage_limit_reached: boolean;
  is_expired: boolean;
}

interface CouponOfferManagerProps {
  businessId: string | null;
}

const CouponOfferManager: React.FC<CouponOfferManagerProps> = ({ businessId }) => {
  const { session } = useAuth();
  const [couponOffers, setCouponOffers] = useState<CouponOffer[]>([]);
  const [couponStats, setCouponStats] = useState<CouponOfferStats[]>([]);
  const [passStats, setPassStats] = useState<{[key: string]: {google: number, apple: number, used: number}}>({});
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<CouponOffer | null>(null);

  // localStorage key for builder state
  const BUILDER_STATE_KEY = `couponBuilderState_${businessId}`;

  // Load builder state from localStorage
  const loadBuilderState = () => {
    try {
      const saved = localStorage.getItem(BUILDER_STATE_KEY);
      return saved ? JSON.parse(saved) : { showBuilder: false, selectedOfferId: null };
    } catch (error) {
      console.error('Error loading coupon builder state:', error);
      return { showBuilder: false, selectedOfferId: null };
    }
  };

  const builderState = loadBuilderState();

  const [showBuilder, setShowBuilder] = useState(builderState.showBuilder);
  const [showPreview, setShowPreview] = useState(false);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [selectedOfferForQr, setSelectedOfferForQr] = useState<CouponOffer | null>(null);
  const [offerPreviewTypes, setOfferPreviewTypes] = useState<Record<string, 'google' | 'apple'>>({});

  // Save builder state to localStorage
  useEffect(() => {
    const state = {
      showBuilder,
      selectedOfferId: selectedOffer?.id || null,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(BUILDER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving coupon builder state:', error);
    }
  }, [showBuilder, selectedOffer, BUILDER_STATE_KEY]);

  // Restore selected offer when component mounts
  useEffect(() => {
    if (builderState.selectedOfferId && couponOffers.length > 0) {
      const offerToEdit = couponOffers.find(o => o.id === builderState.selectedOfferId);
      if (offerToEdit) {
        setSelectedOffer(offerToEdit);
      }
    }
  }, [couponOffers, builderState.selectedOfferId]);

  useEffect(() => {
    // Reset all state when businessId changes
    setCouponOffers([]);
    setCouponStats([]);
    setLoading(true);

    // Reset creation and UI states (but preserve builder state from localStorage)
    // Don't reset showBuilder - it's managed by localStorage
    setShowPreview(false);
    // Don't reset selectedOffer - it will be restored from localStorage if needed
    setIsQrDialogOpen(false);
    setSelectedOfferForQr(null);
    setOfferPreviewTypes({});

    if (businessId) {
      fetchCouponOffers();
    } else {
      setLoading(false);
    }
  }, [businessId]);

  const fetchCouponOffers = async () => {
    if (!businessId) return;

    try {
      // Fetch coupon offers (exclude soft-deleted)
      const { data: offers, error: offersError } = await supabase
        .from('coupon_offers')
        .select('*')
        .eq('business_id', businessId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (offersError) throw offersError;

      setCouponOffers(offers || []);

      // Fetch coupon stats if there are offers
      if (offers && offers.length > 0) {
        const { data: stats, error: statsError } = await supabase
          .from('coupon_offer_stats')
          .select('*')
          .in('coupon_offer_id', offers.map(o => o.id));

        if (!statsError) {
          setCouponStats(stats || []);
        }

        // Fetch pass statistics
        await fetchPassStats(offers.map(o => o.id));
      } else {
        setCouponStats([]);
        setPassStats({});
      }
    } catch (error) {
      console.error('Error fetching coupon offers:', error);
      toast.error('Failed to load coupon offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPassStats = async (offerIds: string[]) => {
    if (!offerIds || offerIds.length === 0) return;

    try {
      // Fetch distributed passes
      const { data: passData, error: passError } = await supabase
        .from('passes')
        .select('coupon_offer_id, pass_type_identifier, pass_data')
        .in('coupon_offer_id', offerIds)
        .not('coupon_offer_id', 'is', null)
        .eq('status', 'active');

      if (passError) throw passError;

      // Fetch redemption counts from coupon_redemptions table
      const { data: redemptionData, error: redemptionError } = await supabase
        .from('coupon_redemptions')
        .select('coupon_offer_id')
        .in('coupon_offer_id', offerIds);

      if (redemptionError) throw redemptionError;

      const stats: {[key: string]: {google: number, apple: number, used: number}} = {};

      // Count distributed passes by platform
      (passData || []).forEach(pass => {
        if (pass.coupon_offer_id) {
          if (!stats[pass.coupon_offer_id]) {
            stats[pass.coupon_offer_id] = { google: 0, apple: 0, used: 0 };
          }

          // Determine platform from pass_type_identifier or pass_data
          const isGooglePass =
            pass.pass_type_identifier === 'pass.com.tag-innovation.google-wallet' ||
            pass.pass_type_identifier === 'pass.com.tag-innovation.google-wallet-offer' ||
            (pass.pass_data && typeof pass.pass_data === 'object' && pass.pass_data.platform === 'google');

          if (isGooglePass) {
            stats[pass.coupon_offer_id].google++;
          } else {
            stats[pass.coupon_offer_id].apple++;
          }
        }
      });

      // Count redemptions (times used)
      (redemptionData || []).forEach(redemption => {
        if (redemption.coupon_offer_id) {
          if (!stats[redemption.coupon_offer_id]) {
            stats[redemption.coupon_offer_id] = { google: 0, apple: 0, used: 0 };
          }
          stats[redemption.coupon_offer_id].used++;
        }
      });

      setPassStats(stats);
    } catch (error) {
      console.error('Error fetching pass stats:', error);
    }
  };

  const handleCreateOffer = () => {
    if (couponOffers.length >= 1) {
      toast.error(
        "Your current plan includes one coupon offer. To add another, please contact us at +421 902 691 939 or tag-innovation1@gmail.com",
        {
          duration: 8000, // Show for 8 seconds
        }
      );
      return;
    }
    setShowBuilder(true);
  };

  const handleBuilderComplete = async (couponOffer: any) => {
    // Refresh data to show the new offer
    await fetchCouponOffers();
    setShowBuilder(false);
    setSelectedOffer(null);

    // Clear builder state from localStorage
    try {
      localStorage.removeItem(BUILDER_STATE_KEY);
    } catch (error) {
      console.error('Error clearing coupon builder state:', error);
    }

    // Show success message
    toast.success('Coupon offer created successfully!');
  };

  const handleEditOffer = (offer: CouponOffer) => {
    setSelectedOffer(offer);
    setShowBuilder(true);
  };

  const handleOfferSaved = () => {
    setShowBuilder(false);
    setSelectedOffer(null);
    fetchCouponOffers();
  };

  const handleDeleteOffer = async (offerId: string) => {
    if (!confirm('Are you sure you want to delete this coupon offer? This action cannot be undone.')) {
      return;
    }

    try {
      // Find the offer to get its image URLs
      const offerToDelete = couponOffers.find(o => o.id === offerId);

      const { error } = await supabase.rpc('soft_delete_coupon_offer', {
        p_offer_id: offerId,
        p_reason: 'Deleted by user'
      });

      if (error) throw error;

      // Delete all images from storage (don't wait for this to complete)
      if (offerToDelete) {
        deleteAllCouponImages(offerToDelete).catch(err => {
          console.error('Error deleting coupon images:', err);
          // Don't show error to user - offer is already deleted from their perspective
        });
      }

      toast.success('Coupon offer deleted successfully');
      fetchCouponOffers();
    } catch (error) {
      console.error('Error deleting coupon offer:', error);
      toast.error('Failed to delete coupon offer');
    }
  };

  const toggleOfferActive = async (offer: CouponOffer) => {
    try {
      const { error } = await supabase
        .from('coupon_offers')
        .update({ is_active: !offer.is_active })
        .eq('id', offer.id);

      if (error) throw error;

      toast.success(`Coupon offer ${!offer.is_active ? 'activated' : 'deactivated'}`);
      fetchCouponOffers();
    } catch (error) {
      console.error('Error toggling offer status:', error);
      toast.error('Failed to update coupon offer status');
    }
  };

  const handleViewQrCode = async (offer: CouponOffer) => {
    setSelectedOfferForQr(offer);
    setIsQrDialogOpen(true);
  };


  const getDiscountDisplay = (offer: CouponOffer) => {
    if (offer.discount_type === 'percentage') {
      return `${offer.discount_value}% off`;
    } else if (offer.discount_type === 'fixed_amount') {
      return `$${offer.discount_value} off`;
    } else {
      return 'Free item';
    }
  };

  const getOfferStats = (offerId: string): CouponOfferStats | undefined => {
    return couponStats.find(s => s.id === offerId);
  };

  const isOfferExpired = (offer: CouponOffer): boolean => {
    if (!offer.valid_until) return false;
    return new Date(offer.valid_until) < new Date();
  };

  const isOfferLimitReached = (offer: CouponOffer): boolean => {
    const stats = getOfferStats(offer.id);
    if (!stats) return false;
    return stats.usage_limit_reached;
  };

  if (loading) {
    return <UnifiedSkeleton variant="tab" />;
  }

  // Show coupon offer builder if in creation mode
  if (showBuilder) {
    return (
      <CouponOfferBuilder
        businessId={businessId || ''}
        offer={selectedOffer}
        onComplete={handleBuilderComplete}
        onCancel={() => {
          setShowBuilder(false);
          setSelectedOffer(null);
          // Clear builder state from localStorage
          try {
            localStorage.removeItem(BUILDER_STATE_KEY);
          } catch (error) {
            console.error('Error clearing coupon builder state:', error);
          }
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Coupon Offers</h2>
      </div>

      <div className="w-full space-y-6">
        {couponOffers.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
            <Card className="max-w-md">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Gift className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center">
                  No coupon offers yet. Create your first offer to attract customers with special discounts!
                </p>
              </CardContent>
            </Card>
            <Button 
              className="flex items-center gap-2"
              onClick={handleCreateOffer}
              size="lg"
            >
              <Plus className="h-4 w-4" />
              Create Coupon Offer
            </Button>
          </div>
        ) : (
          <div className="space-y-6">{couponOffers.map((offer) => {
            const stats = getOfferStats(offer.id);
            const isExpired = isOfferExpired(offer);
            const isLimitReached = isOfferLimitReached(offer);
            
            return (
              <Card key={offer.id} className="overflow-hidden border hover:shadow-lg transition-shadow duration-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary dark:text-primary">
                      <Percent className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold">{offer.name}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {offer.description}
                        <span className="ml-2 text-primary">
                          • {(passStats[offer.id]?.apple || 0) + (passStats[offer.id]?.google || 0)} coupons
                          • {passStats[offer.id]?.used || 0} used
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Frontend edit functionality is ready - just add backend support and remove disabled prop */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditOffer(offer)}
                      disabled={true}
                      className="h-8 w-8 p-0 text-muted-foreground cursor-not-allowed border opacity-50"
                      title="Edit offer (coming soon)"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteOffer(offer.id)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border hover:border-red-400"
                      title="Delete offer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Half: Offer Details + Sharing */}
                    <div className="space-y-10">
                      {/* Offer Details */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold">Offer Details</h4>
                        <div className="space-y-3">
                          <div>
                            <span className="text-sm text-muted-foreground">Discount:</span>
                            <div className="font-semibold text-primary text-base">{getDiscountDisplay(offer)}</div>
                          </div>
                          {offer.minimum_purchase > 0 && (
                            <div>
                              <span className="text-sm text-muted-foreground">Minimum Purchase:</span>
                              <div className="font-medium">${offer.minimum_purchase}</div>
                            </div>
                          )}
                          {offer.valid_until && (
                            <div>
                              <span className="text-sm text-muted-foreground">Valid Until:</span>
                              <div className="font-medium">{new Date(offer.valid_until).toLocaleDateString('en-GB').replace(/\//g, '.')}</div>
                            </div>
                          )}
                          <div>
                            <span className="text-sm text-muted-foreground">Distributed Coupons:</span>
                            <div className="text-sm font-medium flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Apple className="h-4 w-4 text-muted-foreground" />
                                <span>{passStats[offer.id]?.apple || 0} Apple</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Wallet className="h-4 w-4 text-muted-foreground" />
                                <span>{passStats[offer.id]?.google || 0} Google</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <span className="text-sm text-muted-foreground">Times Redeemed:</span>
                            <div className="font-medium">{passStats[offer.id]?.used || 0}</div>
                          </div>
                        </div>
                      </div>

                      {/* Customer & Staff Access Section */}
                      <div className="space-y-4">
                        <h4 className="text-base font-semibold">Customer & Staff Access</h4>

                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Generate limited-use links for your coupon offer and set up staff access.
                          </p>
                          <div className="space-y-2">
                            <Button
                              size="sm"
                              onClick={() => handleViewQrCode(offer)}
                              className="w-full bg-primary hover:bg-primary/90"
                            >
                              <Share className="h-3 w-3 mr-2" />
                              Manage Limited-Use Links
                            </Button>
                            
                            <BusinessScannerCredentialsManager
                              businessId={businessId || ''}
                              resourceType="coupon"
                              resourceId={offer.id}
                              resourceName={offer.name}
                              onUpdate={() => {
                                // Refresh offers if needed
                                fetchCouponOffers();
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Half: Pass Preview */}
                    <div className="-mt-4 space-y-1">
                      <div className="w-full flex justify-center pt-2">
                        <UnifiedPassPreview
                          key={`${offer.id}-${offerPreviewTypes[offer.id] || 'google'}`}
                          passData={{
                            type: 'coupon_simple',
                            businessName: offer.header || offer.name,
                            name: offer.name,
                            programName: offer.name,
                            header: offer.header,
                            description: offer.description,
                            background_color: offer.background_color || PRIMARY_COLOR,
                            // Use new multi-resolution fields with fallback to legacy fields
                            apple_logo_urls: offer.apple_logo_urls,
                            google_logo_url: offer.google_logo_url || offer.logo_url || '',
                            hero_image_urls: offer.hero_image_urls,
                            // Legacy fields for backward compatibility
                            logo_url: offer.logo_url || '',
                            hero_image_url: offer.hero_image_url || '',
                            discount_type: offer.discount_type,
                            discount_value: offer.discount_value,
                            valid_until: offer.valid_until,
                            minimum_purchase: offer.minimum_purchase
                          }}
                          mode="viewing"
                          showPlatformToggle={false}
                          defaultPlatform={offerPreviewTypes[offer.id] || 'google'}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* Create button below existing offers */}
          <div className="flex justify-center pt-4">
            <Button 
              className="flex items-center gap-2"
              onClick={handleCreateOffer}
              size="lg"
            >
              <Plus className="h-4 w-4" />
              Create Coupon Offer
            </Button>
          </div>
        </div>
      )}

      </div>

      {/* Share & Download Dialog */}
      {selectedOfferForQr && (
        <ShareDownloadDialog
          couponOffer={selectedOfferForQr}
          isOpen={isQrDialogOpen}
          onClose={() => setIsQrDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default CouponOfferManager;